import { useEffect, useState } from "react";
import { Card, Form, Spinner, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faFileExcel,
  faMagnifyingGlass,
  faRotateLeft,
  faPenToSquare,
  faTrashCan,
  faBoxesStacked,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";
import * as XLSX from "xlsx";
import "./css/ToolStock.css";

export default function StockToolsPage() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("ALL");
  const [keyword, setKeyword] = useState("");

  // State modal edit
  const [showModal, setShowModal] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [stockQty, setStockQty] = useState(0);
  const [spec, setSpec] = useState("-");
  const [saving, setSaving] = useState(false);
  const [kode, setKode] = useState(""); // pastikan ini di atas
  const [partNama, setPartNama] = useState(""); // pastikan ini di atas
  const [editCategory, setEditCategory] = useState("");
  const [isAddMode, setIsAddMode] = useState(false);

  // Kategori filter (dikelola admin, tersimpan di backend)
  const [categories, setCategories] = useState([]);
  const [showCatModal, setShowCatModal] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [savingCat, setSavingCat] = useState(false);

  // Ambil data stock tools
  const fetchTools = async () => {
    setLoading(true);
    try {
      const res = await api.get("/stock-tools");
      setTools(res.data.data || []);
    } catch (err) {
      console.error("Gagal ambil tools:", err);
    } finally {
      setLoading(false);
    }
  };

  // Ambil daftar kategori dari backend
  const fetchCategories = async () => {
    try {
      const res = await api.get("/stock-tools/categories");
      setCategories(res.data.data || []);
    } catch (err) {
      console.error("Gagal ambil kategori:", err);
    }
  };

  useEffect(() => {
    fetchTools();
    fetchCategories();
  }, []);

  // Tambah kategori baru (admin)
  const handleAddCategory = async () => {
    const name = newCat.trim().toUpperCase();
    if (!name) {
      popup.alert("Nama kategori wajib diisi");
      return;
    }
    setSavingCat(true);
    try {
      const res = await api.post("/stock-tools/categories", { name });
      if (res.data.success) {
        setNewCat("");
        setShowCatModal(false);
        fetchCategories();
      } else {
        popup.alert(res.data.message || "Gagal menambah kategori");
      }
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menambah kategori");
    } finally {
      setSavingCat(false);
    }
  };

  // Hapus kategori (admin)
  const handleDeleteCategory = async (name) => {
    const inUse = tools.some(
      (t) => t.category?.toUpperCase().trim() === name,
    );
    if (inUse) {
      popup.alert(
        `Kategori "${name}" masih dipakai oleh tools, jadi tidak bisa dihapus. ` +
          `Ubah kategori tools tersebut dulu.`,
      );
      return;
    }
    const ok = await popup.confirm(`Hapus kategori "${name}" dari filter?`);
    if (!ok) return;
    try {
      const res = await api.delete(
        `/stock-tools/categories/${encodeURIComponent(name)}`,
      );
      if (res.data.success) {
        if (category === name) setCategory("ALL");
        fetchCategories();
        popup.success("Data berhasil dihapus");
      } else {
        popup.alert(res.data.message || "Gagal menghapus kategori");
      }
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menghapus kategori");
    }
  };

  // Filter tools
  const filteredTools = tools.filter((t) => {
    const matchCategory =
      category === "ALL" || t.category?.toUpperCase() === category;
    const matchKeyword =
      !keyword ||
      [t.kode, t.part_name, t.category, t.spec, t.lokasi, t.stock_qty, t.status]
        .filter((v) => v !== null && v !== undefined)
        .join(" ")
        .toLowerCase()
        .includes(keyword.toLowerCase());
    return matchCategory && matchKeyword;
  });

  const getStockStatus = (qty) => {
    if (qty <= 0) return "O";
    if (qty <= 2) return "LOW";
    return "OK";
  };

  // ... kode sebelumnya tetap sama sampai bagian modal

  const handleAddClick = () => {
    setIsAddMode(true);
    setEditTool(null);
    setKode("");
    setPartNama("");
    setStockQty(0);
    setSpec("");
    setEditCategory("");
    setShowModal(true);
  };

  // Buka modal edit
  const handleEditClick = (tool) => {
    setEditTool(tool);
    setStockQty(tool.stock_qty);
    setSpec(tool.spec || "-");
    setKode(tool.kode);
    setPartNama(tool.part_name);
    setEditCategory(tool.category?.toUpperCase().trim());
    setShowModal(true);
  };

  // Simpan update stock + kode + part_name
  const handleSave = async () => {
    setSaving(true);
    const categoryValue = editCategory.trim().toUpperCase();
    if (!categoryValue) {
      popup.alert("Kategori harus diisi!");
      setSaving(false);
      return;
    }

    try {
      let res;

      if (isAddMode) {
        // CREATE
        res = await api.post("/stock-tools", {
          kode,
          part_nama: partNama,
          stock_qty: stockQty,
          category: categoryValue,
          spec,
        });
      } else {
        // UPDATE
        res = await api.put(`/stock-tools/${editTool.id}`, {
          kode,
          part_nama: partNama,
          stock_qty: stockQty,
          category: categoryValue,
          spec,
        });
      }

      if (res.data.success) {
        // Daftarkan kategori baru ke daftar filter bila belum ada
        if (categoryValue && !categories.includes(categoryValue)) {
          try {
            await api.post("/stock-tools/categories", { name: categoryValue });
          } catch (e) {
            /* abaikan bila kategori sudah ada */
          }
        }
        setShowModal(false);
        setEditTool(null);
        setIsAddMode(false);
        fetchTools();
        fetchCategories();
        popup.success(
          isAddMode ? "Data berhasil disimpan" : "Data berhasil diperbarui",
        );
      } else {
        popup.alert(res.data.message || "Gagal menyimpan data");
      }
    } catch (err) {
      console.error(err);
      popup.alert("Terjadi kesalahan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tool) => {
    const ok = await popup.confirm(`Yakin ingin menghapus ${tool.part_name}?`);

    if (!ok) return;

    try {
      const res = await api.delete(`/stock-tools/${tool.id}`);

      if (res.data.success) {
        fetchTools();
        popup.success("Data berhasil dihapus");
      } else {
        popup.alert(res.data.message || "Gagal menghapus data");
      }
    } catch (err) {
      console.error(err);
      popup.alert("Terjadi kesalahan saat menghapus");
    }
  };

  const handleDownloadExcel = () => {
    if (filteredTools.length === 0) {
      popup.alert("Data kosong, tidak ada yang bisa di-download");
      return;
    }

    const data = filteredTools.map((t, i) => ({
      No: i + 1,
      Kode: t.kode,
      "Part Name": t.part_name,
      Spec: t.spec || "-",
      Kategori: t.category,
      Stock: t.stock_qty,
      Status: getStockStatus(t.stock_qty),
      "Update Terakhir": t.updated_at
        ? new Date(t.updated_at).toLocaleString("id-ID")
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Tools");

    XLSX.writeFile(workbook, "DATA_STOCK_TOOLS.xlsx");
  };

  const sortedTools = [...filteredTools].sort((a, b) => {
    if (a.stock_qty === 0 && b.stock_qty !== 0) return -1;
    if (a.stock_qty !== 0 && b.stock_qty === 0) return 1;
    return 0; // selain itu urutan tetap
  });

  // Daftar kategori bersumber dari backend (dikelola admin).
  // Kategori apa pun yang sudah dipakai tool tetap ikut tampil agar tidak ada
  // tool yang "hilang" dari filter meski kategorinya belum terdaftar.
  const categoryOptions = [
    "ALL",
    ...Array.from(
      new Set([
        ...categories,
        ...tools.map((t) => t.category?.toUpperCase().trim()).filter(Boolean),
      ]),
    ),
  ];

  return (
    <div className="longlife-container d-flex flex-column">
      <Card className="shadow-sm flex-grow-1 d-flex flex-column">
        {/* ================= HEADER ================= */}
        <Card.Header className="st-header">
          {/* JUDUL + BUTTON */}
          <div className="st-header-top">
            <div className="st-title-wrap">
              <span className="st-icon">
                <FontAwesomeIcon icon={faBoxesStacked} />
              </span>
              <div>
                <h5 className="st-title">Data Stock Tools</h5>
                <p className="st-subtitle">
                  {filteredTools.length} item &middot; kelola stok &amp; kategori
                </p>
              </div>
            </div>

            <div className="st-actions">
              <button className="st-btn st-btn-add" onClick={handleAddClick}>
                <FontAwesomeIcon icon={faPlus} /> Tambah Tools
              </button>
              <button
                className="st-btn st-btn-excel"
                onClick={handleDownloadExcel}
              >
                <FontAwesomeIcon icon={faFileExcel} /> Export Excel
              </button>
            </div>
          </div>

          {/* FILTER + SEARCH */}
          <div className="st-toolbar">
            <div className="st-chips">
              {categoryOptions.map((cat) => (
                <span
                  key={cat}
                  className={`st-chip ${category === cat ? "active" : ""}`}
                >
                  <button
                    className="st-chip-label"
                    onClick={() => setCategory(cat)}
                  >
                    {cat}
                  </button>
                  {cat !== "ALL" && (
                    <button
                      className="st-chip-del"
                      title={`Hapus kategori ${cat}`}
                      onClick={() => handleDeleteCategory(cat)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}

              {/* Tambah kategori (hanya admin) */}
              <button
                className="st-add-cat"
                onClick={() => setShowCatModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} /> Kategori
              </button>
            </div>

            <div className="st-search">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="st-search-icon"
              />
              <input
                className="st-search-input"
                placeholder="Cari tools..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              {(keyword || category !== "ALL") && (
                <button
                  className="st-reset"
                  title="Reset filter"
                  onClick={() => {
                    setCategory("ALL");
                    setKeyword("");
                  }}
                >
                  <FontAwesomeIcon icon={faRotateLeft} />
                </button>
              )}
            </div>
          </div>
        </Card.Header>

        {/* ================= TABLE ================= */}
        <Card.Body>
          <div className="table-container">
            <table className="preview-table">
              <thead className="sticky-top">
                <tr>
                  <th>No</th>
                  <th>Part Name</th>
                  <th>Kode / Spec</th>
                  <th>Kategori</th>
                  <th className="text-center">Stock</th>
                  <th className="text-center">Aksi</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-4">
                      <Spinner animation="border" size="sm" /> Loading...
                    </td>
                  </tr>
                ) : filteredTools.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center">
                      Data tidak ditemukan
                    </td>
                  </tr>
                ) : (
                  sortedTools.map((t, i) => {
                    return (
                      <tr
                        key={t.id}
                        className={t.stock_qty === 0 ? "stock-zero" : ""}
                      >
                        <td className="text-center st-muted">{i + 1}</td>
                        <td className="st-partname cell-left">{t.part_name}</td>
                        <td className="cell-left">{t.spec || "-"}</td>
                        <td>
                          {t.category}
                        </td>
                        <td className="text-center">
                          <b>{t.stock_qty}</b>
                        </td>

                        <td>
                          <div className="st-row-actions">
                            <button
                              className="st-icon-btn st-edit"
                              title="Edit"
                              onClick={() => handleEditClick(t)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                            <button
                              className="st-icon-btn st-del"
                              title="Hapus"
                              onClick={() => handleDelete(t)}
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* Modal Edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Stock Tool</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Kode</Form.Label>
            <Form.Control
              value={kode}
              onChange={(e) => setKode(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Part Name</Form.Label>
            <Form.Control
              value={partNama}
              onChange={(e) => setPartNama(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Spec</Form.Label>
            <Form.Control
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Kategori</Form.Label>
            <Form.Control
              list="tool-category-options"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value.toUpperCase())}
              placeholder="Pilih atau ketik kategori baru"
            />
            <datalist id="tool-category-options">
              {categoryOptions
                .filter((c) => c !== "ALL")
                .map((c) => (
                  <option key={c} value={c} />
                ))}
            </datalist>
            <Form.Text className="text-muted">
              Bisa memilih dari daftar atau mengetik kategori baru — kategori
              baru otomatis muncul sebagai filter.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Stock Qty</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={stockQty}
              onChange={(e) => setStockQty(Number(e.target.value))}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Tambah Kategori (admin) */}
      <Modal
        show={showCatModal}
        onHide={() => setShowCatModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Tambah Kategori</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nama Kategori</Form.Label>
            <Form.Control
              autoFocus
              value={newCat}
              onChange={(e) => setNewCat(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCategory();
              }}
              placeholder="Contoh: OUTER"
            />
            <Form.Text className="text-muted">
              Kategori langsung muncul sebagai tombol filter.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCatModal(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleAddCategory}
            disabled={savingCat}
          >
            {savingCat ? "Menyimpan..." : "Tambah"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
